import * as React from "react"
import { formatCurrencyInput, parseCurrencyValue } from "@limo/shared"
import { cn } from "@/lib/utils"

export interface CurrencyInputProps
  extends Omit<React.ComponentProps<"input">, "onChange" | "value" | "type"> {
  /** The numeric value (will be displayed formatted) */
  value?: number | null
  /** Called with the numeric value when input changes */
  onChange?: (value: number | null) => void
  /** Whether to allow negative values (default: false) */
  allowNegative?: boolean
  /** Minimum allowed value */
  min?: number
  /** Maximum allowed value */
  max?: number
}

/**
 * CurrencyInput component with automatic formatting and $ prefix.
 * Displays values formatted with thousand separators (e.g., "$1,234.56").
 * Returns numeric values to the onChange handler.
 *
 * @example
 * <CurrencyInput
 *   value={field.value}
 *   onChange={field.onChange}
 *   placeholder="0.00"
 * />
 */
const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ className, value, onChange, allowNegative = false, min, max, placeholder = "0.00", ...props }, ref) => {
    // Convert numeric value to display string
    const getDisplayValue = (numValue: number | null | undefined): string => {
      if (numValue === null || numValue === undefined) {
        return ""
      }
      // Format the number with proper decimal places
      const formatted = numValue.toFixed(2)
      return formatCurrencyInput(formatted)
    }

    // Local state for the display value
    const [displayValue, setDisplayValue] = React.useState(() => getDisplayValue(value))

    // Sync display value when external value changes
    React.useEffect(() => {
      // Only update if the parsed values are different to avoid cursor jumping
      const currentParsed = parseCurrencyValue(displayValue)
      const newValue = value ?? 0
      if (currentParsed !== newValue) {
        setDisplayValue(getDisplayValue(value))
      }
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value

      // Allow empty input
      if (rawValue === "") {
        setDisplayValue("")
        onChange?.(null)
        return
      }

      // Remove the dollar sign if user somehow typed it
      const cleanedInput = rawValue.replace(/^\$/, "")

      // Format the input
      const formatted = formatCurrencyInput(cleanedInput)
      setDisplayValue(formatted)

      // Parse and send numeric value
      const numericValue = parseCurrencyValue(formatted)

      // Apply min/max constraints
      let constrainedValue = numericValue
      if (!allowNegative && constrainedValue < 0) {
        constrainedValue = 0
      }
      if (min !== undefined && constrainedValue < min) {
        constrainedValue = min
      }
      if (max !== undefined && constrainedValue > max) {
        constrainedValue = max
      }

      onChange?.(constrainedValue)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // On blur, ensure proper formatting with 2 decimal places
      if (displayValue !== "") {
        const numericValue = parseCurrencyValue(displayValue)
        const formatted = formatCurrencyInput(numericValue.toFixed(2))
        setDisplayValue(formatted)
      }
      props.onBlur?.(e)
    }

    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
          $
        </span>
        <input
          type="text"
          inputMode="decimal"
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-transparent pl-7 pr-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            className
          )}
          ref={ref}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          {...props}
        />
      </div>
    )
  }
)
CurrencyInput.displayName = "CurrencyInput"

export { CurrencyInput }
